
/**
 * The admin navigation, as data.
 *
 * Kept separate from the shell that renders it so the structure can be asserted
 * directly — the old markup-only navbar made it impossible to tell whether a
 * route had quietly fallen out of the menu.
 *
 * A section with `children` renders as a collapsible group; one without renders
 * as a single row. `state` is react-router location state, which a few of the
 * user lists read to decide which profile type they are showing.
 *
 * No icons: the Admin Panel v3 rail is text-only, and nested children are set
 * apart by indentation alone (see the design system's Examples/Admin Shell).
 *
 * Every entry here must correspond to a route registered in App.js — a menu row
 * pointing at an unregistered path silently redirects to `/`. navItems.test.js
 * enforces that by reading App.js's route registrations.
 */
export const buildNavSections = ({
  isPermanentJobV2Enabled = false,
  isBestInDentalEnabled = false,
} = {}) => [
  {
    id: 'dashboard',
    label: 'Dashboard',
    children: [
      { id: 'dash-list', label: 'List', to: '/dash-list' },
      { id: 'dash-metrics', label: 'Metrics', to: '/dash-metrics' },
    ],
  },
  {
    id: 'users',
    label: 'Users',
    children: [
      { id: 'offices', label: 'Offices', to: '/dentist', state: { profile: 'DNT' } },
      { id: 'dso-offices', label: 'DSO Offices', to: '/dso', state: { profile: 'DNT' } },
      {
        id: 'professionals',
        label: 'Professionals',
        to: '/hygienist',
        state: { profile: 'HYG' },
      },
      { id: 'unfinished', label: 'Unfinished users', to: '/unfinished' },
      // 'Office Staff' (/office-staff) is deliberately not here. The old
      // bootstrap navbar linked to it, but no such route has ever been
      // registered in App.js, so the entry silently redirected to `/`. Restore
      // it when the route exists.
      { id: 'dso-controls', label: 'DSO Controls', to: '/dso-controls' },
    ],
  },
  {
    id: 'active-offers',
    label: 'Active Offers',
    children: [
      { id: 'multiday', label: 'Multiday', to: '/multiday' },
      { id: 'temp-shifts', label: 'Temp Shifts', to: '/jobs' },
      // Both of these sit behind the same GrowthBook flag as the routes
      // themselves, so a hidden route can never be reachable from the menu.
      ...(isPermanentJobV2Enabled
        ? [{ id: 'interviews', label: 'Interviews', to: '/interviews' }]
        : []),
      { id: 'permanent-jobs', label: 'Permanent Jobs', to: '/pp-jobs' },
      ...(isPermanentJobV2Enabled
        ? [{ id: 'perm-product-jobs', label: 'Perm Product Jobs', to: '/permanent-jobs' }]
        : []),
    ],
  },
  {
    id: 'licenses',
    label: 'Licenses',
    to: '/licenses',
  },
  {
    id: 'sms',
    label: 'SMS Campaign',
    to: '/sms',
  },
  {
    id: 'messages',
    label: 'Messages',
    to: '/messages',
  },
  {
    id: 'referral',
    label: 'Referral',
    to: '/referral',
  },
  {
    // The id and the routes stay `pro-programs`: GOTU-4818 renames what the
    // rail says, not where the pages live, and /pro-programs/* are real URLs
    // people already have. Keeping the id aligned with the route is the honest
    // pairing — the label is the only thing the ticket asks to change.
    id: 'pro-programs',
    label: 'Programs',
    children: [
      { id: 'pro-promotions', label: 'Promotions', to: '/pro-programs/promotions' },
      { id: 'pro-challenges', label: 'Challenges', to: '/pro-programs/challenges' },
      // Behind the same flag as its routes in App.js — an entry pointing at an
      // unregistered route silently redirects to `/`.
      // It does not live under /pro-programs, so isSectionActive matches it by
      // membership in this array rather than by URL prefix, which is why the
      // group still auto-opens on /best-in-dental.
      ...(isBestInDentalEnabled
        ? [{ id: 'best-in-dental', label: 'Best in Dental', to: '/best-in-dental' }]
        : []),
    ],
  },
];

/**
 * Whether `to` is the route currently being shown.
 *
 * Prefix-matches so a detail route keeps its parent highlighted (for example
 * `/best-in-dental/:cycleId` keeps "Best in Dental" selected), while stopping at
 * a segment boundary so `/dso` does not also light up for `/dso-controls`.
 */
export const isRouteActive = (pathname, to) =>
  pathname === to || pathname.startsWith(`${to}/`);

/** True when any child of a group is the active route, so the group opens itself. */
export const isSectionActive = (pathname, section) =>
  (section.children ?? []).some((child) => isRouteActive(pathname, child.to));
